import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getUserUsage, type UserUsageSummaryResponse } from '../api/client';
import { useAuth } from '../context/AuthContext';
import McpUsageAnalytics from './McpUsageAnalytics';

function isoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function presetRange(days: number): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - (days - 1));
  return {
    from: isoDate(start),
    to: isoDate(now),
  };
}

export default function UserUsageAnalytics() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const initialRange = useMemo(() => presetRange(30), []);
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [data, setData] = useState<UserUsageSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [mcpRefresh, setMcpRefresh] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getUserUsage(fromDate, toDate));
    } catch (err: any) {
      setData(null);
      setError(err?.response?.data?.detail || t('settings.usage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyPreset = (days: number) => {
    const range = presetRange(days);
    setFromDate(range.from);
    setToDate(range.to);
  };

  const toggleUser = (userId: string) => {
    setExpandedUsers((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const formatTimestamp = (value?: string | null) => {
    if (!value) return t('settings.usage.never');
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: data?.timezone,
    }).format(new Date(value));
  };

  const cards = data ? [
    [t('settings.usage.activeMembers'), `${data.totals.active_members}/${data.totals.total_members}`],
    [t('settings.usage.pageViews'), data.totals.page_views.toLocaleString()],
    [t('settings.usage.actions'), data.totals.actions.toLocaleString()],
    [t('settings.usage.averageActiveDays'), data.totals.average_active_days.toLocaleString()],
  ] : [];

  return (
    <section className="space-y-5" data-testid="user-usage-analytics">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex gap-2">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => applyPreset(days)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('settings.usage.days', { count: days })}
            </button>
          ))}
        </div>
        <label className="text-sm text-gray-600">
          <span className="mb-1 block">{t('settings.usage.from')}</span>
          <input
            type="date"
            value={fromDate}
            max={toDate}
            onChange={(event) => setFromDate(event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2"
          />
        </label>
        <label className="text-sm text-gray-600">
          <span className="mb-1 block">{t('settings.usage.to')}</span>
          <input
            type="date"
            value={toDate}
            min={fromDate}
            max={isoDate(new Date())}
            onChange={(event) => setToDate(event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2"
          />
        </label>
        <button
          type="button"
          onClick={() => { void load(); setMcpRefresh((value) => value + 1); }}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          {t('common.refresh')}
        </button>
        {data && (
          <span className="text-xs text-gray-400">
            {t('settings.usage.timezoneNote', { timezone: data.timezone })}
          </span>
        )}
      </div>

      {loading && (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-400">
          {t('common.loading')}
        </div>
      )}
      {!loading && error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}
      {!loading && data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {cards.map(([label, value]) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-sm text-gray-500">{label}</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 font-semibold text-gray-900">{t('settings.usage.dailyActivity')}</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="page_views" name={t('settings.usage.pageViews')} stroke="#2563eb" fill="#bfdbfe" />
                  <Area type="monotone" dataKey="actions" name={t('settings.usage.actions')} stroke="#059669" fill="#a7f3d0" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-start">{t('settings.usage.member')}</th>
                    <th className="px-4 py-3 text-start">{t('settings.usage.lastActive')}</th>
                    <th className="px-4 py-3 text-end">{t('settings.usage.activeDays')}</th>
                    <th className="px-4 py-3 text-end">{t('settings.usage.pageViews')}</th>
                    <th className="px-4 py-3 text-end">{t('settings.usage.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.users.map((user) => (
                    <Fragment key={user.user_id}>
                      <tr
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleUser(user.user_id)}
                        data-testid={`usage-user-${user.user_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email} · {user.role}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{formatTimestamp(user.last_active_at)}</td>
                        <td className="px-4 py-3 text-end">{user.active_days}</td>
                        <td className="px-4 py-3 text-end">{user.page_views}</td>
                        <td className="px-4 py-3 text-end">{user.actions}</td>
                      </tr>
                      {expandedUsers.has(user.user_id) && (
                        <tr key={`${user.user_id}-features`}>
                          <td colSpan={5} className="bg-gray-50 px-6 py-4">
                            {user.features.length ? (
                              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {user.features.map((feature) => (
                                  <div key={feature.feature} className="rounded border border-gray-200 bg-white p-3">
                                    <div className="font-medium text-gray-800">
                                      {t(`settings.usage.features.${feature.feature}`)}
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">
                                      {t('settings.usage.featureCounts', {
                                        pageViews: feature.page_views,
                                        actions: feature.actions,
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">{t('settings.usage.noActivity')}</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {!data.users.length && (
              <p className="p-8 text-center text-sm text-gray-500">{t('settings.usage.noMembers')}</p>
            )}
          </div>
        </>
      )}
      {user?.tenant_id && <McpUsageAnalytics
        key={JSON.stringify([user.tenant_id, user.id, fromDate, toDate])}
        tenantId={user.tenant_id} from={fromDate} to={toDate} refresh={mcpRefresh}
      />}
    </section>
  );
}
